// The Board: the whole factory on one screen. The desktop kanban's columns
// (Backlog → Closed, human gates interleaved with in-flight stages) rendered as
// a vertical list of collapsible sections. Park a new big task straight from the
// header; groom the backlog, then watch work flow down the columns live.
import { el, clear, isInteracting } from "../dom.js";
import { Connection } from "../relay.js";
import { bottomNav } from "../nav.js";
import { projectTitle } from "../topbar.js";
import { notificationBell } from "../bell.js";
import { pendingCount } from "./notifications.js";
import type { DaemonEntry } from "../store.js";
import type { Attention, BigTask, Task } from "../types.js";
import { renderBoard } from "./board-columns.js";

const REFETCH_DEBOUNCE_MS = 500;

export function renderBoardView(
  root: HTMLElement,
  entry: DaemonEntry,
  onSwitch: () => void,
  onFeed: () => void,
  onSettings: () => void,
  onNotifications: () => void,
): () => void {
  clear(root);

  const dot = el("span", { class: "status-dot" });
  const banner = el("div", {});
  const composeSlot = el("div", {});
  const list = el("div", {}, [el("div", { class: "empty" }, ["Connecting…"])]);

  let bell = notificationBell({ count: 0, onClick: onNotifications });
  const updateBell = (count: number) => {
    const next = notificationBell({ count, onClick: onNotifications });
    bell.replaceWith(next);
    bell = next;
  };

  root.append(
    el("header", { class: "topbar" }, [
      projectTitle(dot, entry.name, "Board", onSwitch),
      el("button", { class: "primary", onclick: () => toggleCompose() }, ["＋ New"]),
      bell,
    ]),
    banner,
    composeSlot,
    list,
    bottomNav({ active: "board", onFeed, onBoard: () => {}, onSettings }),
  );

  let conn: Connection | null = null;
  let stopped = false;
  let refetchTimer = 0;
  let reconnectDelay = 1000;

  const setOnline = (online: boolean) => {
    dot.className = `status-dot ${online ? "online" : "offline"}`;
  };

  const refetch = async () => {
    if (!conn) return;
    try {
      // Two bounded /api/tasks calls instead of one unfiltered fetch: the active
      // lifecycle (few) plus the most recent merged+closed (capped daemon-side),
      // so the relayed response stays under the 900 KiB relay cap once a project
      // has a few hundred tasks. Concatenated into one array so every column path
      // in board-columns.ts keeps working — Merged/Closed just show the latest 50.
      const [att, bigTasks, activeTasks, doneTasks] = await Promise.all([
        conn.api<Attention>("GET", "/api/attention"),
        conn.api<BigTask[]>("GET", "/api/bigtasks"),
        conn.api<Task[]>("GET", "/api/tasks?status=ready,claimed,running,verifying,review,blocked,failed,planned"),
        conn.api<Task[]>("GET", "/api/tasks?status=merged,closed&limit=50"),
      ]);
      const tasks = [...(activeTasks ?? []), ...(doneTasks ?? [])];
      updateBell(pendingCount(att ?? emptyAttention()));
      clear(list);
      list.append(renderBoard(att ?? emptyAttention(), bigTasks ?? [], tasks, {
        api: conn.api.bind(conn),
        onChanged: scheduleRefetch,
      }));
      banner.replaceChildren();
    } catch (e) {
      banner.replaceChildren(el("div", { class: "error-banner" }, [(e as Error).message]));
    }
  };

  const scheduleRefetch = () => {
    clearTimeout(refetchTimer);
    refetchTimer = window.setTimeout(() => {
      // Don't rebuild the list out from under someone reading or typing — retry
      // on the next tick so the update still lands once they're done.
      if (isInteracting(list)) return scheduleRefetch();
      void refetch();
    }, REFETCH_DEBOUNCE_MS);
  };

  // Compose: park a new big task straight from the board — same idiom as the
  // backlog composer it replaces (status "backlog"), so it lands in the Backlog
  // column for grooming.
  let composeOpen = false;
  const toggleCompose = () => {
    composeOpen = !composeOpen;
    composeSlot.replaceChildren();
    if (!composeOpen) return;

    const title = el("input", { type: "text", placeholder: "What should get built? (outcome)" }) as HTMLInputElement;
    const intent = el("textarea", { placeholder: "Why — context, constraints, desired outcome…" }) as HTMLTextAreaElement;
    const err = el("div", {});
    const submit = el("button", { class: "primary" }, ["Add to backlog"]) as HTMLButtonElement;
    submit.onclick = async () => {
      err.replaceChildren();
      if (!conn) {
        err.replaceChildren(el("div", { class: "error-banner" }, ["Not connected to the daemon yet."]));
        return;
      }
      if (!title.value.trim()) {
        err.replaceChildren(el("div", { class: "error-banner" }, ["Give it a title first."]));
        return;
      }
      submit.disabled = true;
      try {
        await conn.api("POST", "/api/bigtasks", {
          title: title.value.trim(),
          intent: intent.value.trim(),
          status: "backlog",
        });
        toggleCompose();
        banner.replaceChildren(el("div", { class: "notice" }, ["Parked in the backlog."]));
        scheduleRefetch();
      } catch (e) {
        submit.disabled = false;
        err.replaceChildren(el("div", { class: "error-banner" }, [(e as Error).message]));
      }
    };

    composeSlot.append(
      el("div", { class: "card" }, [
        el("h3", {}, ["New big task"]),
        el("div", { class: "field" }, [title]),
        el("div", { class: "field" }, [intent]),
        err,
        el("div", { class: "actions" }, [
          submit,
          el("button", { onclick: () => toggleCompose() }, ["Cancel"]),
        ]),
      ]),
    );
    title.focus();
  };

  const connect = async () => {
    if (stopped) return;
    banner.replaceChildren();
    const c = new Connection(entry, {
      onEvent: () => scheduleRefetch(),
      onClose: () => {
        setOnline(false);
        if (stopped) return;
        banner.replaceChildren(el("div", { class: "notice" }, ["Connection lost — reconnecting…"]));
        window.setTimeout(() => void connect(), reconnectDelay);
        reconnectDelay = Math.min(reconnectDelay * 2, 30_000);
      },
    });
    try {
      await c.connect();
      conn = c;
      reconnectDelay = 1000;
      setOnline(true);
      await refetch();
    } catch (e) {
      setOnline(false);
      clear(list);
      list.append(el("div", { class: "empty" }, ["Daemon unreachable."]));
      banner.replaceChildren(
        el("div", { class: "error-banner" }, [(e as Error).message]),
        el("div", { class: "actions" }, [el("button", { onclick: () => void connect() }, ["Retry"])]),
      );
    }
  };
  void connect();

  return () => {
    stopped = true;
    clearTimeout(refetchTimer);
    conn?.close();
  };
}

function emptyAttention(): Attention {
  return { reviews: [], decisions: [], audits: [], plans: [] };
}
